import { DeepPartial, Time } from 'lightweight-charts'
import {
    ChartCandle,
    IndicatorDrawCallback,
    IndicatorLineCallback,
    Series,
    SeriesOptions,
    SeriesOutput
} from './chart.typings'
import {
    DataUpdateRequestType,
    fitDrawingData,
    getIndicatorDrawingSettings,
    createIndicatorData,
    getTimeIndex, CalendarData
} from '../../util/chart'
import { VOLUME_INDICATOR_CODE, VOLUME_SCALE } from '../../assets/constants'
import SeriesGroup, { SeriesGroupOptions } from './seriesGroup'
import { Indicator, IndicatorInput_InputType, IndicatorOutput_OutputType } from '../../proto/generated/indicator'
import { getIndicatorScript } from '../../services/apis/misc'
import { CandleTypes } from './chart.enums'
import { DrawOptions, FillOptions, LineOptions, PlotOptions } from '../../pages/chartPage/ChartPage'
import { convertToTimezone } from '../../util/dateTime'
import { DrawingDisplayType, Point } from './drawingTools/drawing.interface'
import {ActionType} from "../UndoRedoFunctonalities/UndoRedo.context";
import { GetSeriesResponse, GetSeriesResponse_Series } from '../../proto/generated/candle-data-depot'
import { ChartType } from '../../proto/generated/chart'
import { Exchange } from '../../proto/generated/common'
import { getExchangeAndCodeFromKey } from '../../util/security'
import { sanitizeId } from '../../util/indicators'

function sumIndicatorLengthParams(
    inputValues: Record<string, string>,
    inputs: Indicator['inputs']
): number {
    const lengthTypes = new Set([
        IndicatorInput_InputType.INTEGER,
        IndicatorInput_InputType.LENGTH
    ])
    return inputs.reduce((sum, i) => {
        if (!lengthTypes.has(i.type)) return sum
        const raw =
            inputValues[i.code] ??
            inputValues[i.title] ??
            inputValues[sanitizeId(i.code || '')] ??
            inputValues[sanitizeId(i.title || '')]
        if (raw === undefined || raw === '') return sum
        const n = Number(raw)
        if (!Number.isFinite(n)) return sum
        return sum + Math.max(0, Math.floor(Math.abs(n)))
    }, 0)
}

export type IndicatorSeriesGroupOptions = SeriesGroupOptions & {
    calendarData?: CalendarData;
}
export default class IndicatorSeriesGroup extends SeriesGroup {

    titleSeriesIdMap: Record<string, string> = {}
    indicator: Indicator
    outputs: SeriesOutput[] = []
    indicatorScript: string = ''
    intervalId: NodeJS.Timeout | null = null
    indicatorSeriesData: GetSeriesResponse['series'] = [];
    stopBackwardDataFetch = 0;
    stopForwardDataFetch = 0;
    maxLength = 2;
    evaluationLength = 500;
    currentEndOffset = 0;
    previousDataLength = 0;
    updateStart = 0;
    updateEnd = 0;
    calendarData?: CalendarData;
    /** Plot titles the script drew on the last evaluation (see recordIndicatorPlotActivity). */
    activePlotTitles = new Set<string>()

    constructor(options: IndicatorSeriesGroupOptions) {
        super(options)
        this.titleSeriesIdMap = this.series.reduce((acc, sr) => {
            return {
                ...acc,
                [sr.title]: sr.id
            }
        }, {} as Record<string, string>)
        const [indicator] = this.chart?.indicators?.filter(v => v.code === options.config.scriptCode)
        this.indicator = indicator
        this.onConfigUpdate();
        this.calendarData = options.calendarData;
        if (this.indicator.refreshIntervalInSeconds > 0) {
            this.intervalId = setInterval(() => {
                this.updateData(true, DataUpdateRequestType.INITIAL)
            }, this.indicator.refreshIntervalInSeconds * 1000)
        }
        this.outputs = indicator.outputs.filter(v => [IndicatorOutput_OutputType.LINE, IndicatorOutput_OutputType.HISTOGRAM, IndicatorOutput_OutputType.AREA, IndicatorOutput_OutputType.COLUMN, IndicatorOutput_OutputType.HZ_LINE, IndicatorOutput_OutputType.FILL].indexOf(v.type) !== -1)
    }

    async initialize() {
        await super.initialize()
        if (this.indicatorScript) return
        void getIndicatorScript({ code: this.config.scriptCode })
            .then((scriptResponse) => {
                this.indicatorScript = scriptResponse.script
                if (this.chart.getRawData()?.length) {
                    void this.updateData(true, DataUpdateRequestType.INITIAL)
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    reset() {
        this.stopBackwardDataFetch = 0;
        this.stopForwardDataFetch = 0;
        this.indicatorSeriesData = [];
        this.currentEndOffset = 0;
        this.previousDataLength = 0;
    }

    onConfigUpdate() {
        this.maxLength = sumIndicatorLengthParams(this.config.inputValues, this.indicator.inputs)
        if (this.maxLength > this.evaluationLength) {
            this.evaluationLength = this.maxLength;
        }
    }

    clearTimeInterval() {
        if (this.intervalId) {
            clearInterval(this.intervalId)
            this.intervalId = null // Reset the interval ID to null
        }
    }

    getSeriesOptions(type: ChartType, config: Series): DeepPartial<SeriesOptions> {
        const options = super.getSeriesOptions(type, config);
        const settings = this.chart.chartConfig?.scalesAndLinesSettings
        const showValue =
            (settings?.showIndicatorValue ?? true) && (this.config.showValueOnPriceScale ?? true)
        const showLabel =
            (settings?.showIndicatorLabel ?? true) && (this.config.showLabelOnPriceScale ?? true)
        const noLabel = type === ChartType.FILL || config.drawing
        const titleOption = (!noLabel && showLabel) ? config.title : ''
        const lastValueVisible = noLabel ? false : showValue
        switch(type) {
            case ChartType.LINE:
            case ChartType.LINE_BREAK:
            case ChartType.AREA:
            case ChartType.COLUMN:
            case ChartType.HISTOGRAM:
            case ChartType.CHART_TYPE_UNKNOWN:
                return {...options, source: "series", lastValueVisible, title: titleOption}
        }
        return {...options, lastValueVisible, title: titleOption}
    }

    private applyVolumeScaleMargins() {
        if (this.config.scriptCode === VOLUME_INDICATOR_CODE && this.chart.chartApi && !(this.chart as any).destroyed) {
            this.chart.chartApi.priceScale(VOLUME_SCALE).applyOptions({
                scaleMargins: {
                    top: 0.8,
                    bottom: 0
                }
            })
        }
    }

    createAllSeries(): void {
        super.createAllSeries()
        this.applyVolumeScaleMargins()
    }

    recreateSeries(id: string) {
        super.recreateSeries(id)
        this.applyVolumeScaleMargins()
    }

    componentToHex(c: number): string {
        let hex = c.toString(16)
        return hex.length === 1 ? '0' + hex : hex
    }

    destroy(isHistoryData:boolean=false) {
        this.clearTimeInterval()
        super.destroy(isHistoryData)
        const data={
            chartId: this.chart.id,
            indicator: {
                ...this.indicator
            } as Indicator,
            seriesGroupId: this.config.id
        }
       if (!isHistoryData) {
            this.chart.historyManager.push(data, "INDICATOR REMOVE", ActionType.REMOVE_INDICATOR);
        }
    }

    generateSimilarColor(originalColor: string, deviation: number): string {
        // Convert hexadecimal color to RGB
        let r = parseInt(originalColor.substring(1, 3), 16)
        let g = parseInt(originalColor.substring(3, 5), 16)
        let b = parseInt(originalColor.substring(5, 7), 16)
        // Apply deviations to the original RGB values
        let multiplier = (1 + deviation / 100)
        r = Math.floor(r * (1 + deviation / 100) > 255 ? r * (1 - deviation / 100) : r * (1 + deviation / 100))
        g = Math.floor(g * (1 + deviation / 100) > 255 ? g * (1 - deviation / 100) : g * (1 + deviation / 100))
        b = Math.floor(b * (1 + deviation / 100) > 255 ? b * (1 - deviation / 100) : b * (1 + deviation / 100))
        // b = Math.min(255, Math.max(0, Math.floor(b * multiplier)));
        // Convert back to hexadecimal format
        return '#' + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b) + (originalColor.substring(7))
    }

    customStringToIntHashInRange(input: string, range: number): number {
        let hash = 0
        if (input.length === 0) return hash
        for (let i = 0; i < input.length; i++) {
            const char = input.charCodeAt(i)
            hash = ((hash << 5) - hash) + char
            hash |= 0 // Convert to 32-bit integer
        }
        // Map hashed value to the desired range
        return hash % range // Ensure the result is non-negative
    }

// Helper function to convert RGB component to hexadecimal

    plot = (updateType: DataUpdateRequestType, options: PlotOptions): number[] => {
        let candles = this.chart.getPrimaryData()
        const newData = options.series;
        const newReferenceData = options.referenceSeries || [];
        const offset = options.offset || 0;
        if (!newData.length) return [];
        let updatedSeries = updateType === DataUpdateRequestType.LIVE ? newData.slice(-1) : newData;
        let updatedReferenceData = updateType === DataUpdateRequestType.LIVE ? newReferenceData.slice(-1) : newReferenceData;
        const seriesData = this.chartSeries[this.titleSeriesIdMap[options.title]].getData()
        const indicatorLength = updatedSeries.length;
        const candleLength = candles.length;
        let data: ChartCandle[] = [];
        if (updateType !== DataUpdateRequestType.LIVE) {
            data = new Array(candleLength);
    
            for (let c = 0; c < this.updateStart; c++) {
                const series = seriesData[c]
                if (series !== undefined) {
                    data[c] = series
                }
            }
            let updateLength = this.updateEnd - this.updateStart + 1;
            for (let c = 0; c < updateLength; c++) {
                const series = updatedSeries[indicatorLength - 1 - c]
                const referenceSeries = updatedReferenceData[indicatorLength - 1 - c]
                const candleIndex = this.updateEnd - c + offset;
                
                if (series !== undefined && candleIndex >= 0 && candleIndex < candleLength && candles[candleIndex]) {
                    data[candleIndex] = {...candles[candleIndex], series, referenceSeries};
                }
            }

            updateLength = candleLength - (this.updateEnd + 1);

            const seriesLength = seriesData.length;
            for (let c = 0; c < updateLength; c++) {
                const series = seriesData[seriesLength - 1 - c]
                const candleIndex = candleLength - 1 - c;
                if (series !== undefined) {
                    data[candleIndex] = series
                }
            }
    
            const seriesId = this.titleSeriesIdMap[options.title]
            if (seriesId && this.chartSeries[seriesId]) {
                if (options.display === 'NONE') {
                    this.chartSeries[seriesId].updateOptions({ visible: false })
                } else if (options.isHline || updatedSeries.length > 0) {
                    this.chartSeries[seriesId].updateOptions({ visible: true })
                }
            }
        } else {
            const series = updatedSeries[0]
            const referenceSeries = updatedReferenceData[0]
            if (series !== undefined) {
                const targetIndex = candles.length - 1 + offset;
                if (targetIndex >= 0 && targetIndex < candles.length) {
                    data[0] = {...candles[targetIndex], ...{series, referenceSeries}}
                }
            }
        }
        this.updateSeriesData(updateType, options.title, data, {...options, series: undefined})
        return newData

    }

    fill = (updateType: DataUpdateRequestType, options: FillOptions) => {
        if (!options.seriesA || !options.seriesB) return;
        const newSeriesAData = options.seriesA
        const newSeriesBData = options.seriesB
        let candles = this.chart.getPrimaryData()
        let updatedSeriesAData = updateType === DataUpdateRequestType.LIVE ? newSeriesAData.slice(-1) : newSeriesAData;
        let updatedSeriesBData = updateType === DataUpdateRequestType.LIVE ? newSeriesBData.slice(-1) : newSeriesBData;
        const seriesData = this.chartSeries[this.titleSeriesIdMap[options.title]]?.getData() ?? []
        const indicatorLength = updatedSeriesAData.length;
        const candleLength = candles.length;

        let data: ChartCandle[] = [];

        if (updateType !== DataUpdateRequestType.LIVE) {
            data = new Array(candles.length);

            for (let c = 0; c < this.updateStart; c++) {
                const series = seriesData[c]
                if (series !== undefined) {
                    data[c] = series
                }
            }

            let updateLength = this.updateEnd - this.updateStart + 1;

            for (let c = 0; c < updateLength; c++) {
                let pointA = updatedSeriesAData[indicatorLength - 1 - c]
                let pointB = updatedSeriesBData[indicatorLength - 1 - c]
                const candleIndex = this.updateEnd - c;

                data[candleIndex] = {...candles[candleIndex], fillHigh: pointA, fillLow: pointB}
            }

            updateLength = candleLength - (this.updateEnd + 1);

            const seriesLength = seriesData.length;
            for (let c = 0; c < updateLength; c++) {
                const series = seriesData[seriesLength - 1 - c]
                const candleIndex = candleLength - 1 - c;
                if (series !== undefined) {
                    data[candleIndex] = series
                }
            }
        } else {
            let pointA = updatedSeriesAData[0]
            let pointB = updatedSeriesBData[0]

            data[0] = candles[candles.length - 1];
            data[0].fillHigh = pointA
            data[0].fillLow = pointB
        }

        this.updateSeriesData(updateType, options.title, data, {...options, seriesA: undefined, seriesB: undefined})
    }

    line: IndicatorLineCallback = (options: LineOptions) => {
        const drawing = this.seriesDrawings[this.titleSeriesIdMap[options.title]]
        const points = options.dataPoints.map((dp) => {
            return [{
                x: convertToTimezone(dp.startTime / 1000, this.chart.timezone) as Time,
                y: dp.val,
                id: `${dp.startTime}-${dp.val}`
            },
                {
                    x: convertToTimezone(dp.endTime / 1000, this.chart.timezone) as Time,
                    y: dp.val,
                    id: `${dp.endTime}-${dp.val}`
                },
                `${options.title}( ${dp.val.toPrecision(6)} )`
            ] as [Point, Point, string]
        })

        if (!drawing) {
            const pSg = this.chart?.primarySeriesGroup
            const dr = this.chart.drawingManager.addDrawing('simpleIndicatorLine', DrawingDisplayType.INDICATOR ,{
                drawingType: 'simpleIndicatorLine',
                persist: false
            }, {
                lineType: 'normal',
                lineColor: { color: options.color ?? '', opacity: 1 },
                lineWidth: (window.devicePixelRatio || 1),
                indicator: true,
                data: points,
                statsPosition: 'Left',
                showStats: false
            }, undefined, undefined, pSg?.pane?.id, pSg?.series?.[0]?.id)
            if (dr !== null) {
                this.seriesDrawings[this.titleSeriesIdMap[options.title]] = dr!
            }
        } else {
            drawing.updateSettings({
                ...drawing._settings,
                points: points,
                lineColor: { color: options.color ?? '', opacity: 1 }
            })
            drawing.primitive?.requestUpdate()
        }
    }

    getPrimarySecurity = () => {
        return this.chart.securities?.[0] ?? (() => {
            const securityKey = this.chart.primarySeriesGroup?.config?.securityList?.[0]
            if (securityKey) {
                const [exchange, code] = getExchangeAndCodeFromKey(securityKey)
                return { code, exchange, priceMultiplier: 100 }
            }
            return { code: 'unknown', exchange: Exchange.NSE, priceMultiplier: 100 }
        })()
    }

    draw: IndicatorDrawCallback = (options: DrawOptions) => {
        const primarySecurity = this.getPrimarySecurity()
        const drawing = this.seriesDrawings[this.titleSeriesIdMap[options.title]]
        const data = fitDrawingData({
            code: primarySecurity.code,
            exchange: primarySecurity.exchange,
            priceMultiplier: primarySecurity.priceMultiplier ?? 100
        }, options.type, options.data)
        if (!drawing) {
            const pSg = this.chart?.primarySeriesGroup
            const dr = this.chart.drawingManager.addDrawing(options.type, DrawingDisplayType.INDICATOR, {
                drawingType: options.type,
                persist: false
            }, {
                ...getIndicatorDrawingSettings(options.type, this.chart.chartApi.options(), this.config.outputValues[this.titleSeriesIdMap[options.title]].drawingSettings ?? {}),
                data
            }, undefined, undefined, pSg?.pane?.id, pSg?.series?.[0]?.id)
            if (dr !== null) {
                this.seriesDrawings[this.titleSeriesIdMap[options.title]] = dr!
            }
        } else {
            drawing.updateSettings({
                ...drawing._settings,
                data: data
            })
            drawing.primitive?.requestUpdate()
        }
    }

    getOutputSeriesForSource(outputCode: string): number[] | undefined {
        const seriesId = this.config.series.find(
            (sid) => this.chart.chartConfig.series[sid]?.code === outputCode
        )
        if (!seriesId) return undefined

        const chartData = this.chartSeries[seriesId]?.getData() ?? []
        if (!chartData.length) return undefined

        return chartData.map((candle) => candle?.series ?? Number.NaN)
    }

    private getEvaluationCandleStartIndex(updateType: DataUpdateRequestType | undefined, candleCount: number): number {
        if (updateType === DataUpdateRequestType.HISTORICAL) {
            return this.updateStart
        }
        if (updateType === DataUpdateRequestType.LIVE) {
            return Math.max(0, candleCount - this.evaluationLength)
        }
        return 0
    }

    private clearSeriesGroupError() {
        if (!this.config.error && !this.chart.seriesGroupErrors[this.config.id]) {
            return
        }
        this.config.error = undefined
        this.chart.setSeriesGroupError(this.config.id, '')
    }

    updateData = async (newData: boolean, updateType?: DataUpdateRequestType) => {
        this.currentEndOffset = 0;
        if ((updateType === DataUpdateRequestType.LIVE && this.indicator.candleType !== 'STOCK') && !this.chart.chartConfig?.replayMode) {
            return
        }

        const primarySecurity = this.getPrimarySecurity()

        if (updateType === DataUpdateRequestType.INITIAL) {
            this.indicatorSeriesData = [];
            this.stopBackwardDataFetch = 0;
            this.stopForwardDataFetch = 0;
        }
        let data = this.chart.getPrimaryData() || []
        if (data.length === 0) {
            this.indicatorSeriesData = [];
            Object.keys(this.chartSeries).forEach(seriesId => {
                const series = this.chartSeries[seriesId];
                if (series && typeof series._setData === 'function') {
                    try {
                        series._setData([]);
                    } catch (error) {
                        console.warn('Failed to clear indicator series data:', error);
                    }
                }
            });
            return;
        }
        if (updateType === DataUpdateRequestType.HISTORICAL) {
            this.setUpdateRange()
            data = data.slice(this.updateStart, this.updateEnd + 1);
        } else if (updateType === DataUpdateRequestType.LIVE) {
            data = data.slice(-this.evaluationLength)
        } else {
            this.updateEnd = data.length - 1
        }
        try {
            const nextActivePlotTitles = new Set<string>()
            const primaryDataLength = this.chart.getPrimaryData()?.length ?? data.length
            const candleStartIndex = this.getEvaluationCandleStartIndex(updateType, primaryDataLength)
            await createIndicatorData({
                candles: data,
                indicator: this.indicator,
                candleType: this.indicator.candleType == 'OI' ? CandleTypes.OI : this.indicator.candleType === 'STOCK' ? CandleTypes.OHLC : CandleTypes.OTHER,
                inputValues: this.config.inputValues,
                outputValues: this.config.outputValues,
                data: this.indicatorSeriesData,
                stopBackwardFetch: this.stopBackwardDataFetch > 1,
                stopForwardFetch: this.stopForwardDataFetch > 1,
                script: this.indicatorScript,
                calendarData: this.calendarData,
                candleStartIndex,
                timeBucket: {
                    timeSeries: this.chart.timeBucket.timeSeries,
                    value: this.chart.timeBucket.timeValue
                },
                security: {
                    code: primarySecurity.code,
                    exchange: primarySecurity.exchange,
                    priceMultiplier: primarySecurity.priceMultiplier ?? 100
                },
                resolveIndicatorOutputSeries: (ref, candleCount) => {
                    const producer = this.chart.seriesGroupRef[ref.seriesGroupId]
                    if (!(producer instanceof IndicatorSeriesGroup)) {
                        return undefined
                    }
                    const fullSeries = producer.getOutputSeriesForSource(ref.outputCode)
                    if (!fullSeries?.length) {
                        return undefined
                    }
                    const start = candleStartIndex
                    const end = start + candleCount
                    if (start >= fullSeries.length) {
                        return undefined
                    }
                    return fullSeries.slice(start, Math.min(end, fullSeries.length))
                }
            }, {
                plotCallback: (options) => this.plot(updateType!, options),
                drawCallback: this.draw,
                lineCallback: this.line,
                fillCallback: (options) => this.fill(updateType!, options),
                onDataLoad: this.updateIndicatorData,
                plotActivityTracker: nextActivePlotTitles,
            })
            this.activePlotTitles = nextActivePlotTitles
            this.chart.callbacks.onIndicatorPlotTitlesUpdated?.(this.config.id)
            this.clearSeriesGroupError()

        } catch (e) {
            const error = e instanceof Error ? e.message : 'Unknown error occurred while creating indicator data';
            if (this.config.error === error && this.chart.seriesGroupErrors[this.config.id] === error) return;
            this.onConfigUpdate()
            this.chart.setSeriesGroupError(this.config.id, error)
            console.error('chart: Error in creating indicator data', error)
            this.activePlotTitles = new Set()
            this.chart.callbacks.onIndicatorPlotTitlesUpdated?.(this.config.id)
        }
        this.previousDataLength = data.length;
        
    }

    updateIndicatorData = (data: GetSeriesResponse_Series[], stopBackwardDataFetch?: boolean, stopForwardDataFetch?: boolean) => {
        this.indicatorSeriesData = data;
        if (stopBackwardDataFetch !== undefined) {
            this.stopBackwardDataFetch = stopBackwardDataFetch ? this.stopBackwardDataFetch += 1 : 0
        }
        if (stopForwardDataFetch !== undefined) {
            this.stopForwardDataFetch = stopForwardDataFetch ? this.stopForwardDataFetch += 1 : 0
        }
    }

    updateSeriesData(updateType: DataUpdateRequestType, title: string,  seriesData: ChartCandle[], metadata: Record<string, any> = {}) {
        const seriesId = this.titleSeriesIdMap[title];
        const series = this.chartSeries[seriesId];

        if (!series) return;

        try {
            if (updateType === DataUpdateRequestType.LIVE) {
                series._update(seriesData[seriesData.length - 1], metadata);
            } else {
                series._setData(seriesData, metadata)
            }
        } catch (error) {
            console.error('chart: Series might not exist', error)
        }
        this.chart.callbacks.onDataUpdate()
    }

    // set the range for which indicator neds to be calculated
    setUpdateRange() {
        const data = this.chart.getPrimaryData();
        const dataLength = data.length;

        let updateStart = 0;
        let updateEnd = dataLength - 1;

        for (let id in this.chartSeries) {
            const series = this.chartSeries[id];
            const seriesData = series.getData();
            const seriesLength = seriesData.length;
            if (!seriesLength || !seriesData[0] || !seriesData[seriesLength - 1]) continue;
            const prevEndIndex = getTimeIndex(data , seriesData[0].time as number);
            const nextStartIndex = getTimeIndex(data , seriesData[seriesLength - 1].time as number);

            if (nextStartIndex < dataLength - 1 && prevEndIndex > 0) {
                updateStart = 0;
                updateEnd = dataLength - 1;
            } else if (nextStartIndex < dataLength - 1) {
                updateStart = updateStart === 0 ? nextStartIndex : Math.min(updateStart, nextStartIndex);
                updateEnd = dataLength - 1;
            } else {
                updateStart = 0;
                updateEnd = updateEnd === dataLength - 1 ? prevEndIndex: Math.max(updateEnd, prevEndIndex);
            }
        }

        this.updateStart = Math.max(0, updateStart - this.maxLength);
        this.updateEnd = Math.min(dataLength - 1, updateEnd + this.maxLength);
    }
}